import React, { CSSProperties, useId, useMemo } from "react";
import { classArr } from "../helper/style-helper";
import * as styles from "./weighted-table.module.scss";

export type WeightedTableColumnAlignment = "start" | "center" | "end";

export interface WeightedTableColumnLayout {
	width?: number;
	flex?: number;
	alignment?: WeightedTableColumnAlignment;
}

export function weightedTableColumnAlignmentToTextAlign(
	alignment: WeightedTableColumnAlignment,
): string {
	switch (alignment) {
		case "start":
			return "left";
		case "center":
			return "center";
		case "end":
			return "right";
	}
}

export interface WeightedTableProps {
	style?: CSSProperties;
	className?: string;
	children?: React.ReactNode;
	layout?: readonly WeightedTableColumnLayout[];
}

export function WeightedTable(props: WeightedTableProps) {
	const layout = useMemo(() => props.layout ?? [], [props.layout]);

	const columnStyles = useMemo(() => {
		return layout.map((column): CSSProperties => {
			if (column.width !== undefined) {
				return {
					width: column.width,
				};
			} else {
				// The table has its table layout set to fixed, and will distribute
				// any flexible space proportionally to the width of the columns.
				// As long as the resolved width value is greater than the render width
				// of the column, this will work like flex does.
				return {
					width: (column.flex ?? 1) * 100 + "%",
				};
			}
		});
	}, [layout]);

	const tableID = useId();
	const tableStyles = useMemo(() => {
		//Create styles for use with alignment
		const columnStyles: string[] = layout
			.map((column, index) => {
				if (column.alignment === undefined) {
					return null;
				}

				const childIndex = index + 1;
				return `#${tableID} td:nth-child(${childIndex}), #${tableID} th:nth-child(${childIndex}) {
				text-align: ${weightedTableColumnAlignmentToTextAlign(column.alignment)};
			}`;
			})
			.filter((item) => item !== null);

		if (columnStyles.length === 0) {
			return null;
		}

		return columnStyles.join("\n");
	}, [layout, tableID]);

	return (
		<>
			{tableStyles && <style>{tableStyles}</style>}
			<table
				id={tableID}
				style={props.style}
				className={classArr(styles.table, props.className)}
			>
				<colgroup>
					<col className={styles.spacerColumn} />
					{columnStyles.map((columnStyles, idx) => (
						<col key={idx} style={columnStyles} />
					))}
					<col className={styles.spacerColumn} />
				</colgroup>

				{props.children}
			</table>
		</>
	);
}
